/**
 * Job Status Constants
 * Mirrors the status groupings from Constants.cs in Beauparc.Portal.Features.Services
 */

export const JobStatuses = {
  // Individual statuses
  Unallocated: 'Unallocated',
  Assigned: 'Assigned',
  Confirmed: 'Confirmed',
  PendingPrices: 'Pending Prices',
  Complete: 'Complete',
  Invoiced: 'Invoiced',
  Rejected: 'Rejected',
  Missed: 'Missed',
  Cancelled: 'Cancelled',
  FinancialConfirmation: 'Financial Confirmation',
  GradingConfirmation: 'Grading Confirmation',
  DestinationConfirmation: 'Destination Confirmation',
  Printed: 'Printed',
  InProgress: 'InProgress',
  Exclusion: 'Exclusion',
  RolledBack: 'Rolled Back',
  PendingWeighing: 'Pending Weighing',
  Voided: 'Voided',

  // Status groupings (built from properties to maintain single source of truth)
  get CompletedStatusList(): string[] {
    return [
      this.Complete,
      this.Confirmed,
      this.FinancialConfirmation,
      this.GradingConfirmation,
      this.DestinationConfirmation,
      this.PendingPrices,
      this.RolledBack,
      this.PendingWeighing,
      this.Invoiced,
    ];
  },

  get ScheduledStatusList(): string[] {
    return [this.Unallocated, this.Assigned, this.Printed, this.InProgress];
  },

  get RejectedStatusList(): string[] {
    return [this.Rejected, this.Missed, this.Cancelled, this.Exclusion, this.Voided];
  },
};

/**
 * Get the icon modifier class for a status label
 * Maps status to the appropriate styling based on list membership
 */
export function getStatusIconModifier(statusLabel: string | undefined): 'assigned' | 'complete' | 'rejected' {
  if (!statusLabel) return 'assigned';

  // Normalize case for comparison
  const normalizedLabel = statusLabel.trim();

  if (
    JobStatuses.CompletedStatusList.some(
      s => s.localeCompare(normalizedLabel, undefined, { sensitivity: 'base' }) === 0
    )
  ) {
    return 'complete';
  }

  if (
    JobStatuses.RejectedStatusList.some(s => s.localeCompare(normalizedLabel, undefined, { sensitivity: 'base' }) === 0)
  ) {
    return 'rejected';
  }

  return 'assigned';
}

/**
 * Get the status color for display in the UI
 * Maps status to the appropriate CSS color class based on list membership
 */
export function getStatusColor(statusLabel: string | undefined): 'green' | 'green-outline' | 'red' {
  if (!statusLabel) return 'green-outline';

  // Normalize case for comparison
  const normalizedLabel = statusLabel.trim();

  if (
    JobStatuses.CompletedStatusList.some(
      s => s.localeCompare(normalizedLabel, undefined, { sensitivity: 'base' }) === 0
    )
  ) {
    return 'green';
  }

  if (
    JobStatuses.RejectedStatusList.some(s => s.localeCompare(normalizedLabel, undefined, { sensitivity: 'base' }) === 0)
  ) {
    return 'red';
  }

  // Scheduled is default
  return 'green-outline';
}
